import { createStore } from 'vuex';
import { checkAuth } from '@/authService';
import { markActivity, sessionIdle } from '@/utils/activity';
import { markSignedIn } from '@/utils/signInMark';
import { extractUserRoles } from '@/utils/jwt';
import apiClient from '@/apiClient';
import navigation from './modules/navigation';
import filters from './modules/filters';

export default createStore({
  modules: {
    navigation,
    filters
  },
  state() {
    return {
      isLoggedIn: false,
      projectCreated: false,
      timeOut: false,
      // the session ended after minutes without an action (utils/activity.js): sign-in says so
      idleOut: false,
      isRefreshing: false,
      userInfo: null,
      userRoles: [],
      userPermissions: [],  // 'resource:action', напр. 'feature:onion_sources'
      authInitialized: false // Add flag to track if auth has been initialized
    };
  },
  getters: {
    isAuthenticated: state => state.isLoggedIn,
    isTokenRefreshing: state => state.isRefreshing,
    userRoles: state => state.userRoles,
    isAdmin: state => state.userRoles.includes('admin'),
    hasRole: (state) => (role) => state.userRoles.includes(role),
    userPermissions: state => state.userPermissions,
    // Право-фича: админ проходит всегда, остальные — по списку из /users/me
    hasPermission: (state) => (perm) => state.userRoles.includes('admin') || state.userPermissions.includes(perm),
    isAuthInitialized: state => state.authInitialized
  },
  mutations: {
    setLoggedIn(state, status) {
      state.isLoggedIn = status;
    },
    setProjectCreated(state, value) {
      state.projectCreated = value;
    },
    setIdleOut(state, value) {
      state.idleOut = value;
    },
    setTimeOut(state, value) {
      state.timeOut = value;
    },
    setRefreshing(state, value) {
      state.isRefreshing = value;
    },
    setUserInfo(state, userInfo) {
      state.userInfo = userInfo;
    },
    setUserRoles(state, roles) {
      state.userRoles = roles || [];
    },
    setUserPermissions(state, permissions) {
      state.userPermissions = permissions || [];
    },
    clearAuthState(state) {
      state.isLoggedIn = false;
      state.userInfo = null;
      state.isRefreshing = false;
      state.userRoles = [];
      state.userPermissions = [];
    },
    setAuthInitialized(state, value) {
      state.authInitialized = value;
    }
  },
  actions: {
    async initializeAuth({ commit, dispatch, state }) {
      // Idempotent: repeat calls share one in-flight promise. IMPORTANT: never
      // gate this on isRefreshing (that's the TOKEN-refresh indicator) — the
      // old early-return left authInitialized=false forever and the app hung
      // on the «Инициализация…» screen after logout / boot races.
      if (state.authInitialized) {
        return;
      }
      if (this._authInitPromise) {
        return this._authInitPromise;
      }
      this._authInitPromise = (async () => {
        try {
          // back after the idle limit (the page was left or closed): log out before refreshing
          if (sessionIdle()) {
            await dispatch('logoutIdle');
            return;
          }
          const isAuthenticated = await checkAuth();
          if (isAuthenticated) markActivity(Date.now(), true);
          commit('setLoggedIn', isAuthenticated);

          // If authenticated, fetch user info including roles
          if (isAuthenticated) {
            try {
              const response = await apiClient.get('/users/me');
              const userData = response.data;
              if (userData.roles) {
                const roleNames = userData.roles.map(role => role.name);
                commit('setUserRoles', roleNames);
              } else {
                commit('setUserRoles', []);
              }
              commit('setUserPermissions', userData.permissions || []);
              commit('setUserInfo', userData);
            } catch (error) {
              // If error is 401, user might need to re-authenticate
              if (error.response && error.response.status === 401) {
                commit('setLoggedIn', false);
                commit('setUserRoles', []);
              }
            }
          }

          dispatch('navigation/updateNavigation');
        } catch (error) {
          commit('setLoggedIn', false);
          dispatch('navigation/updateNavigation');
        } finally {
          // ALWAYS complete initialization — the router waits on this flag.
          commit('setAuthInitialized', true);
          this._authInitPromise = null;
        }
      })();
      return this._authInitPromise;
    },
    
    async login({ commit, dispatch }, { access_token, user }) {
      markActivity(Date.now(), true);
      // before the header (and its bell) appears: the windows of notifications come on a sign-in only
      markSignedIn();
      // Store authentication state
      commit('setLoggedIn', true);
      commit('setTimeOut', false);

      // Extract and store user roles from JWT token
      if (access_token) {
        const roles = extractUserRoles(access_token);
        commit('setUserRoles', roles);
      }

      // Fetch full user info from API (login endpoint doesn't return user data)
      try {
        const response = await apiClient.get('/users/me');
        const userData = response.data;
        commit('setUserInfo', userData);
        if (userData.roles) {
          const roleNames = userData.roles.map(role => role.name);
          commit('setUserRoles', roleNames);
        }
      } catch (error) {
        // If user info fetch fails, use provided user data as fallback
        if (user) {
          commit('setUserInfo', user);
        }
      }

      // Update navigation based on new roles
      dispatch('navigation/updateNavigation');
    },
    
    async logout({ commit, dispatch }) {
      console.log('[Store] Executing logout action');
      try {
        // Call server-side logout to clear HTTP-only cookies
        await apiClient.post('/signout');
        console.log('[Store] Server-side logout successful');
      } catch (error) {
        console.error('[Store] Error during server-side logout:', error);
        // Continue with client-side cleanup even if server call fails
      }

      // Clear client-side auth state
      commit('clearAuthState');
      commit('setTimeOut', false);
      // NOTE: Do NOT reset authInitialized - auth system is still initialized,
      // just the user is logged out. This prevents the "Initializing..." screen.

      // Update navigation for logged out state
      dispatch('navigation/updateNavigation');

      console.log('[Store] Logout action completed');
    },
    
    // the session ended because nobody acted for the idle limit
    async logoutIdle({ commit, dispatch }) {
      await dispatch('logout');
      commit('setIdleOut', true);
    },

    handleTokenRefresh({ commit }, isRefreshing) {
      commit('setRefreshing', isRefreshing);
    },
    
    async updateUserRolesFromToken({ commit, dispatch }, accessToken) {
      // Extract and store user roles when token is refreshed
      console.log('[Auth] Updating user roles from refreshed token');
      if (accessToken) {
        const roles = extractUserRoles(accessToken);
        console.log('[Auth] Roles from refreshed token:', roles);
        commit('setUserRoles', roles);
        // Update navigation with new roles
        dispatch('navigation/updateNavigation');
      } else {
        console.warn('[Auth] No access token provided for role update');
      }
    }
  }
});
